"use client";

// src/bar.tsx
import * as React from "react";
import {
  systemForwardRef,
  Base,
  useTheme,
  useStyleConfig
} from "@stepstone-genesis/system";
import { omitProps } from "@stepstone-genesis/utilities";
import {
  useProgressContext,
  useProgressState
} from "@stepstone-genesis/components-progress-state-handler";

// src/styles.ts
import {
  useEmotionInstanceContext
} from "@stepstone-genesis/system";
var useIndeterminateAnimation = () => {
  const { keyframes } = useEmotionInstanceContext();
  return keyframes({
    "0%": { transform: `translateX(-100%)` },
    to: { transform: `translateX(200%)` }
  });
};
var root = {
  bg: "colorBackgroundSecondaryDark",
  borderRadius: "radiusM",
  height: "4px",
  overflow: "hidden",
  width: "100%"
};
var bar = {
  height: "100%",
  transition: "transform 250ms",
  position: "relative",
  borderRadius: "radiusM"
};
var styles = {
  root,
  bar
};

// src/bar.tsx
var LinearProgressBarLayout = systemForwardRef(function LinearProgressBarLayout2({ children, context, ...props }, ref) {
  var _a;
  const { theme } = useTheme();
  const omittedProps = omitProps(props, [
    "isIndeterminate",
    "min",
    "max",
    "value",
    "a11yLabel",
    "isLabelVisible",
    "id"
  ]);
  const { percent, props: ariaProps, isIndeterminate } = context;
  const { PROGRESS_BAR_CONTAINER } = useStyleConfig("PROGRESS_BAR_CONTAINER", {
    styles: {
      base: styles.root
    }
  });
  const { PROGRESS_BAR } = useStyleConfig("PROGRESS_BAR", {
    styles: {
      base: {
        ...isIndeterminate ? {
          backgroundImage: `linear-gradient(to right, transparent 0%, ${(_a = theme.colors) == null ? void 0 : _a.colorPrimaryDefault} 50%, transparent 100%)`
        } : { bg: "colorPrimaryDefault" },
        ...styles.bar
      }
    }
  });
  const animation = useIndeterminateAnimation();
  return /* @__PURE__ */ React.createElement(
    Base,
    {
      as: "div",
      ref,
      element: "progress.linear.container",
      __root: PROGRESS_BAR_CONTAINER.styles,
      ...omittedProps
    },
    /* @__PURE__ */ React.createElement(
      Base,
      {
        ...ariaProps,
        __root: PROGRESS_BAR.styles,
        element: "progress.linear.bar",
        style: {
          ...isIndeterminate ? {
            width: "50%",
            animation: `${animation} 1.5s ease infinite`
          } : {
            transform: `translateX(-${100 - (percent != null ? percent : 0)}%)`
          }
        }
      },
      children
    )
  );
});
var LinearProgressBarNonContext = systemForwardRef(
  function LinearProgressBarNonContext2(props, ref) {
    const nonContextProgressBar = useProgressState(props);
    return /* @__PURE__ */ React.createElement(
      LinearProgressBarLayout,
      {
        context: nonContextProgressBar,
        ref,
        ...props
      }
    );
  }
);
var LinearProgressBar = systemForwardRef(
  function LinearProgressBar2(props, ref) {
    const withinContextProgressBar = useProgressContext();
    return withinContextProgressBar ? /* @__PURE__ */ React.createElement(
      LinearProgressBarLayout,
      {
        context: withinContextProgressBar,
        ref,
        ...props
      }
    ) : /* @__PURE__ */ React.createElement(LinearProgressBarNonContext, { ref, ...props });
  }
);
export {
  LinearProgressBar
};
